import { Injectable } from '@angular/core';
import {
  DomainComponentState,
  QuickSizerApiRequest,
  QuickSizerRequest,
  QS_PRODUCT_NAME,
  QS_VSAN_ESA,
  parseFttRaidKey,
  QS_FTT_RAID_AUTO,
} from '../../feature-modules/quick-sizer/models/quick-sizer.models';
import { Internal } from '../decorators/app.decorators';

const RATIO_WHEN_DISABLED = 1.0;

/** Catalog names of every enabled part. Parts with no catalog row are left out; the engine rejects them. */
function enabledNames(components: DomainComponentState[]): string[] {
  const names = components
    .filter(c => c.enabled)
    .flatMap(c => c.parts.filter(p => !p.missingSizing).map(p => p.productName));
  return [...new Set(names)];
}

/**
 * Puts the single data-reduction ratio on the field the mode uses and pins the other to 1.0,
 * as the forward sizer's `buildVMProfilePayload` does. The engine rejects any other combination.
 */
function ratiosFor(request: QuickSizerRequest): { dedupRatio: number; compressionRatio: number } {
  switch (request.storageEfficiency) {
    case 'COMPRESSION_ONLY':
      return { dedupRatio: RATIO_WHEN_DISABLED, compressionRatio: request.dataReductionRatio };
    case 'DEDUP_AND_COMPRESSION':
      return { dedupRatio: request.dataReductionRatio, compressionRatio: RATIO_WHEN_DISABLED };
  }
}

@Internal()
@Injectable({ providedIn: 'root' })
export class QuickSizerRequestAdapterService {
  toApiRequest(
    request: QuickSizerRequest,
    management: DomainComponentState[],
  ): QuickSizerApiRequest {
    const auto = request.fttRaidKey === QS_FTT_RAID_AUTO;
    const parsed = auto ? null : parseFttRaidKey(request.fttRaidKey);
    if (!auto && !parsed) {
      throw new Error(`Unknown FTT/RAID selection '${request.fttRaidKey}'`);
    }
    const ratios = ratiosFor(request);
    const sockets = request.hardware.sockets || 1;
    const body: QuickSizerApiRequest = {
      product: { name: QS_PRODUCT_NAME, version: request.vcfVersion },
      cluster: { hostCount: request.totalHosts },
      node: {
        cpu: {
          coresPerHost: request.cpuCoresPerHost,
          socketsPerHost: sockets,
          clockSpeedGhz: request.hardware.clockSpeed,
        },
        memory: { systemMemoryPerHostGb: request.systemMemoryGB },
        storage: {
          drivesPerHost: request.hardware.drivesPerHost,
          driveCapacityTb: request.hardware.driveSize,
        },
        network: { speedGbe: request.hardware.networkSpeed },
      },
      vsanConfig: {
        architecture: QS_VSAN_ESA,
        autoMode: auto,
        ...(parsed ? { raid: parsed.raid, hostFailuresToTolerate: parsed.ftt } : {}),
        storageEfficiency: request.storageEfficiency,
        dedupRatio: ratios.dedupRatio,
        compressionRatio: ratios.compressionRatio,
        operationalReserveEnabled: request.operationalReserve,
        hostRebuildReserveEnabled: request.hostRebuildReserve,
      },
      vcfComponents: {
        // Required by the API. Quick Sizer sizes the management domain only.
        numberOfWorkloads: 1,
        managementDomain: request.includeMgmtDomain
          ? { included: true, componentNames: enabledNames(management) }
          : { included: false, componentNames: [] },
        workloadDomain: { included: false, componentNames: [] },
      },
      workloadProfile: { vcpuToCoreRatio: request.vcpuToCoreRatio },
    };
    const systemName = request.hardware.systemName;
    if (request.hardware.mode === 'readynode-based' && systemName) {
      body.readyNodeRef = { systemName };
    }
    return body;
  }
}
